import React, { useState, useEffect, useCallback } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import LoginPage from "./components/LoginPage";
import MainPage from "./components/MainPage";
import AddRecord from "./components/AddRecord";
import Dashboard from "./components/Dashboard";
import ModifyRecords from "./components/ModifyRecords";
import DeleteRecord from "./components/DeleteRecord";
import Logout from "./components/Logout";
import Reset from "./components/reset";
import useDeviceDetect from "./hooks/useDeviceDetect";
import BreakdownTracker from "./components/BreakdownTracker";
import DriverChecklistList from "./components/DriverChecklistList";
import DriverChecklist from "./components/DriverChecklist";
import DriverChecklistView from "./components/DriverChecklistView";
import AddBreakdown from "./components/AddBreakdown";
import EditBreakdown from "./components/EditBreakdown";
import HandleBreakdown from "./components/HandleBreakdown";
import InfoBreakdown from "./components/InfoBreakdown";
import AddUser from "./components/AddUser";
import AddUserForm from "./components/AddUserForm";
import Signup from "./components/Signup";
import DriverList from "./components/DriverList";
import AddDriver from "./components/AddDriver";
import EditDriver from "./components/EditDriver";
import DriverRestDashboard from "./components/DriverRestDashboard";
import DriverDetails from "./components/DriverDetails";
import "./App.css";

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { isMobile } = useDeviceDetect();
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem("token");
      const storedUserRole = localStorage.getItem("userrole");
      console.log("Stored user role:", storedUserRole);
      if (token) {
        try {
          const decoded = jwtDecode(token);
          const isExpired = decoded.exp < Date.now() / 1000;
          setIsAuthenticated(!isExpired);
          setUserRole(storedUserRole || decoded.userrole);
        } catch (error) {
          console.error("Error decoding token:", error);
          setIsAuthenticated(false);
        }
      } else {
        setUserRole(null);
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const ProtectedRoute = useCallback(
    ({ element }) => {
      return isAuthenticated ? element : <Navigate to="/" />;
    },
    [isAuthenticated]
  );

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Router>
      <div className={`app-container ${isMobile ? "mobile" : "desktop"}`}>
        <Routes>
          <Route
            path="/"
            element={<LoginPage setIsAuthenticated={setIsAuthenticated} />}
          />
          <Route path="/reset" element={<Reset />} />
          <Route
            path="/main"
            element={
              <ProtectedRoute element={<MainPage userRole={userRole} />} />
            }
          />
          <Route
            path="/add-record"
            element={
              userRole !== "driver" ? (
                <ProtectedRoute element={<AddRecord />} />
              ) : (
                <Navigate to="/driver-checklists/" />
              )
            }
          />
          <Route
            path="/dashboard"
            element={
              userRole !== "driver" ? (
                <ProtectedRoute element={<Dashboard />} />
              ) : (
                <Navigate to="/driver-checklists/" />
              )
            }
          />
          <Route
            path="/driver-rest-dashboard"
            element={
              userRole !== "driver" ? (
                <ProtectedRoute element={<DriverRestDashboard />} />
              ) : (
                <Navigate to="/driver-checklists/" />
              )
            }
          />
          <Route
            path="/modify-records"
            element={
              userRole !== "driver" ? (
                <ProtectedRoute element={<ModifyRecords />} />
              ) : (
                <Navigate to="/driver-checklists/" />
              )
            }
          />
          <Route
            path="/delete-records"
            element={
              userRole !== "driver" ? (
                <ProtectedRoute element={<DeleteRecord />} />
              ) : (
                <Navigate to="/driver-checklists/" />
              )
            }
          />
          <Route
            path="/breakdown-tracker"
            element={
              userRole !== "driver" ? (
                <ProtectedRoute element={<BreakdownTracker />} />
              ) : (
                <Navigate to="/driver-checklists/" />
              )
            }
          />
          <Route
            path="/driver-checklists/*"
            element={<ProtectedRoute element={<DriverChecklistList />} />}
          />
          <Route
            path="/driver-checklists/new"
            element={<ProtectedRoute element={<DriverChecklist />} />}
          />
          <Route
            path="/driver-checklists/edit/:id"
            element={<ProtectedRoute element={<DriverChecklist />} />}
          />
          <Route
            path="/driver-checklists/view/:id"
            element={<ProtectedRoute element={<DriverChecklistView />} />}
          />
          <Route
            path="/add-breakdown"
            element={<ProtectedRoute element={<AddBreakdown />} />}
          />
          <Route
            path="/edit-breakdown/:id"
            element={<ProtectedRoute element={<EditBreakdown />} />}
          />
          <Route
            path="/handle-breakdown/:id"
            element={<ProtectedRoute element={<HandleBreakdown />} />}
          />
          <Route
            path="/info-breakdown/:id"
            element={<ProtectedRoute element={<InfoBreakdown />} />}
          />
          <Route
            path="/add-user"
            element={<ProtectedRoute element={<AddUser />} />}
          />
          <Route
            path="/add-user-form"
            element={<ProtectedRoute element={<AddUserForm />} />}
          />
          <Route
            path="/add-user-form/:id"
            element={
              <ProtectedRoute element={<AddUserForm isEditing={true} />} />
            }
          />
          <Route path="/signup" element={<Signup />} />
          <Route
            path="/logout"
            element={<Logout setIsAuthenticated={setIsAuthenticated} />}
          />
          <Route
            path="/driver-management"
            element={
              userRole !== "driver" ? (
                <ProtectedRoute element={<DriverList />} />
              ) : (
                <Navigate to="/driver-checklists/" />
              )
            }
          />
          <Route
            path="/add-driver"
            element={
              userRole !== "driver" ? (
                <ProtectedRoute element={<AddDriver />} />
              ) : (
                <Navigate to="/driver-checklists/" />
              )
            }
          />
          <Route
            path="/edit-driver/:id"
            element={
              userRole !== "driver" ? (
                <ProtectedRoute element={<EditDriver />} />
              ) : (
                <Navigate to="/driver-checklists/" />
              )
            }
          />
          <Route
            path="/driver-details/:driverId"
            element={<ProtectedRoute element={<DriverDetails />} />}
          />
        </Routes>
      </div>
    </Router>
  );
};

export default App;
